<template>
  <div
    class="grid gap-4"
    :class="[
      !includeTeacherAssessment && 'grid-cols-1',
      includeTeacherAssessment && 'lg:grid-cols-2',
    ]"
  >
    <div
      class="rounded-large divide-outline-variant flex flex-col divide-y"
      :class="[lessonEnded && 'bg-surface-container', !lessonEnded && 'bg-secondary-container']"
    >
      <div class="p-4">
        <h3 class="text-title-small text-on-surface-variant">
          {{ $t('lumia.lesson.progress.classSelfAssessments') }}
        </h3>
        <span class="text-headline-small text-on-secondary-container">
          <span class="font-semibold">{{
            $t('lumia.lesson.progress.completedCount', {
              given: selfFeedbackGiven.length,
              total: props.members.length,
            })
          }}</span>
          {{ $t('lumia.lesson.progress.completed') }}
        </span>
        <BaseLinearProgressIndictor
          class="mt-1 max-w-md"
          :percentage="selfAssessmentPercentage"
          color="on-secondary-container"
        />
      </div>

      <div class="grow p-4">
        <div class="mb-1 flex flex-row items-center">
          <h3 class="text-title-small text-on-surface-variant grow">
            {{ $t('lumia.lesson.progress.selfAssessmentsMissing') }}
          </h3>
          <div
            class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
          >
            {{ selfFeedbackNotGiven.length }}
          </div>
        </div>
        <div class="flex flex-row flex-wrap gap-1">
          <BaseChip
            v-for="member in visibleMissingMembers"
            v-tooltip="memberFullName(member.member)"
            :key="member.member.id"
            :member="member.member"
            @click="openMemberAccessCode(member.member)"
            >{{ member.member.firstName }}
          </BaseChip>
        </div>
        <div class="mt-2 flex justify-end gap-1">
          <BaseTextButton color="outlined" size="XS" icon="share" @click="openFeedbackLinkDialog">
            {{ $t('lumia.lesson.feedback.showLink') }}
          </BaseTextButton>
          <BaseTextButton
            v-if="hasMoreMissingMembers"
            color="outlined"
            size="XS"
            @click="openDialog"
          >
            <span
              class="text-label-small text-on-surface-variant bg-surface-container-lowest mr-1.5 -ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1"
              >+{{ selfFeedbackNotGiven.length - MAX_CHIPS }}</span
            >{{ $t('lumia.lesson.progress.showAll') }}</BaseTextButton
          >
        </div>
        <BaseDialog
          :title="$t('lumia.lesson.progress.selfAssessmentsMissing')"
          type="basic"
          :is-open="isOpen"
          @close="closeDialog"
        >
          <ul class="flex flex-col pb-2">
            <li v-for="member in selfFeedbackNotGiven" :key="member.member.id">
              <button
                type="button"
                class="flex w-full items-center gap-3 py-2 text-left"
                @click="openMemberAccessCode(member.member)"
              >
                <BaseMemberAvatar :member="member.member" size="32" />
                <span class="text-body-large">{{ memberFullName(member.member) }}</span>
              </button>
            </li>
          </ul>
          <template #dismiss>
            <BaseTextButton @click="closeDialog">{{ $t('common.close') }}</BaseTextButton>
          </template>
        </BaseDialog>
        <LessonFeedbackLinkDialog
          :is-open="feedbackLinkOpen"
          :teams="teams"
          @close="closeFeedbackLinkDialog"
        />
        <MemberAccessCodeDialog
          :is-open="accessCodeOpen"
          :member-id="selectedMember?.id"
          :member-name="selectedMember ? memberFullName(selectedMember) : undefined"
          @close="closeAccessCodeDialog"
        />
      </div>
      <div class="flex h-15 flex-row items-center gap-2 p-4">
        <h3 class="text-title-small text-on-surface-variant">
          {{ $t('lumia.lesson.progress.selfAssessmentsDone') }}
        </h3>
        <div
          class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
        >
          {{ selfFeedbackGiven.length }}
        </div>
        <BaseMemberAvatars
          :class="[lessonEnded && 'bg-surface-container', !lessonEnded && 'bg-secondary-container']"
          :members="selfFeedbackGiven.map((member) => member.member)"
          size="24"
          :display-limit="10"
        />
      </div>
    </div>
    <div
      v-if="props.includeTeacherAssessment"
      class="rounded-large divide-outline-variant flex flex-col divide-y"
      :class="[lessonEnded && 'bg-surface-container', !lessonEnded && 'bg-secondary-container']"
    >
      <div class="p-4">
        <h3 class="text-title-small text-on-surface-variant">
          {{ $t('lumia.lesson.progress.yourAssessments') }}
        </h3>
        <span class="text-headline-small text-on-secondary-container">
          <span class="font-semibold">{{
            $t('lumia.lesson.progress.completedCount', {
              given: teacherFeedbackGiven.length,
              total: props.members.length,
            })
          }}</span>
          {{ $t('lumia.lesson.progress.completed') }}
        </span>
        <BaseLinearProgressIndictor
          class="mt-1"
          :percentage="(teacherFeedbackGiven.length / props.members.length) * 100"
          color="on-secondary-container"
        />
      </div>

      <div class="grow p-4">
        <div class="mb-1 flex flex-row items-center">
          <h3 class="text-title-small text-on-surface-variant grow">
            {{ $t('lumia.lesson.progress.yourAssessmentsMissing') }}
          </h3>
          <div
            class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
          >
            {{ teacherFeedbackNotGiven.length }}
          </div>
        </div>
        <div class="flex flex-row flex-wrap gap-1">
          <BaseChip
            v-for="member in teacherVisibleMissingMembers"
            v-tooltip="memberFullName(member.member)"
            :key="member.member.id"
            :member="member.member"
            >{{ member.member.firstName }}
          </BaseChip>
        </div>
        <div v-if="teacherHasMoreMissingMembers" class="mt-2 flex justify-end">
          <BaseTextButton color="outlined" size="XS" @click="openTeacherDialog">
            <span
              class="text-label-small text-on-surface-variant bg-surface-container-lowest mr-1.5 -ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1"
              >+{{ teacherFeedbackNotGiven.length - MAX_CHIPS }}</span
            >{{ $t('lumia.lesson.progress.showAll') }}</BaseTextButton
          >
        </div>
        <BaseDialog
          :title="$t('lumia.lesson.progress.yourAssessmentsMissing')"
          type="basic"
          :is-open="teacherDialogOpen"
          @close="closeTeacherDialog"
        >
          <ul class="flex flex-col pb-2">
            <li
              v-for="member in teacherFeedbackNotGiven"
              :key="member.member.id"
              class="flex items-center gap-3 py-2"
            >
              <BaseMemberAvatar :member="member.member" size="32" />
              <span class="text-body-large">{{ memberFullName(member.member) }}</span>
            </li>
          </ul>
          <template #dismiss>
            <BaseTextButton @click="closeTeacherDialog">{{ $t('common.close') }}</BaseTextButton>
          </template>
        </BaseDialog>
      </div>
      <div class="flex h-15 flex-row items-center gap-2 p-4">
        <h3 class="text-title-small text-on-surface-variant">
          {{ $t('lumia.lesson.progress.assessmentDone') }}
        </h3>
        <div
          class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
        >
          {{ teacherFeedbackGiven.length }}
        </div>
        <BaseMemberAvatars
          :class="[lessonEnded && 'bg-surface-container', !lessonEnded && 'bg-secondary-container']"
          :members="teacherFeedbackGiven.map((member) => member.member)"
          size="24"
          :display-limit="10"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import type { MemberSelfAssessment } from '../models/self-assessments'
  import type { Member } from '@/core/models/member/member'
  import BaseLinearProgressIndictor from '@/core/components/base/linear-progress-indicator/BaseLinearProgressIndictor.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { memberFullName } from '@/core/models/member/member-name'
  import LessonFeedbackLinkDialog from './LessonFeedbackLinkDialog.vue'
  import MemberAccessCodeDialog from './MemberAccessCodeDialog.vue'

  const MAX_CHIPS = 8

  const props = defineProps<{
    members: ReadonlyArray<MemberSelfAssessment>
    teams: ReadonlyArray<{
      readonly id: string
      readonly name: string
    }>
    includeTeacherAssessment: boolean
    lessonEnded: boolean
  }>()

  const selfFeedbackGiven = computed(() => {
    return props.members.filter((member) => member.answers.length > 0)
  })

  const selfFeedbackNotGiven = computed(() => {
    return props.members.filter((member) => member.answers.length === 0)
  })

  const selfAssessmentPercentage = computed(() => {
    if (props.members.length === 0) {
      return 0
    }
    return (selfFeedbackGiven.value.length / props.members.length) * 100
  })

  const teacherFeedbackGiven = computed(() => {
    return props.members.filter((member) => member.teacherAssessmentGiven)
  })

  const teacherFeedbackNotGiven = computed(() => {
    return props.members.filter((member) => !member.teacherAssessmentGiven)
  })

  const visibleMissingMembers = computed(() => selfFeedbackNotGiven.value.slice(0, MAX_CHIPS))

  const hasMoreMissingMembers = computed(() => selfFeedbackNotGiven.value.length > MAX_CHIPS)

  const teacherVisibleMissingMembers = computed(() =>
    teacherFeedbackNotGiven.value.slice(0, MAX_CHIPS),
  )

  const teacherHasMoreMissingMembers = computed(
    () => teacherFeedbackNotGiven.value.length > MAX_CHIPS,
  )

  const { isOpen, openDialog, closeDialog } = useDialog()
  const {
    isOpen: teacherDialogOpen,
    openDialog: openTeacherDialog,
    closeDialog: closeTeacherDialog,
  } = useDialog()
  const {
    isOpen: feedbackLinkOpen,
    openDialog: openFeedbackLinkDialog,
    closeDialog: closeFeedbackLinkDialog,
  } = useDialog()
  const {
    isOpen: accessCodeOpen,
    openDialog: openAccessCodeDialog,
    closeDialog: closeAccessCodeDialog,
  } = useDialog()

  const selectedMember = ref<Member>()

  const openMemberAccessCode = (member: Member) => {
    selectedMember.value = member
    closeDialog()
    openAccessCodeDialog()
  }
</script>
